import { useState } from 'react';

import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { CapabilitySchedule } from '@/hooks/useCapabilitySchedule';

interface DeleteScheduleModalProps {
  schedule: CapabilitySchedule | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduleId: string) => Promise<void>;
}

/**
 * DeleteScheduleModal - Confirmation modal for schedule deletion
 *
 * Displays a warning message and confirmation button for deleting
 * a capability schedule. Handles loading state during deletion.
 */
export function DeleteScheduleModal({
  schedule,
  isOpen,
  onClose,
  onConfirm,
}: DeleteScheduleModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!schedule) return;
    setIsDeleting(true);
    try {
      await onConfirm(schedule.scheduleId);
      onClose();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!schedule) return null;

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onClose}
      onConfirm={handleDelete}
      title="Delete Schedule"
      description={`Are you sure you want to delete the schedule for ${schedule.capabilityName}? Deleting this schedule will permanently remove it and stop all future scheduled executions.`}
      confirmLabel="Delete"
      loadingLabel="Deleting..."
      isLoading={isDeleting}
    />
  );
}
